import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { GlobalStat } from '@shared/index';
import { RAGAI_DEBUG_EVENT_PREFIXES } from '@shared/ragai.schemas';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DebugSocketService } from 'src/app/shared/services/debug-socket.service';
import { EventsStringService } from 'src/app/shared/services/events-string.service';
import { WsClient } from '@shared/index';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EventManagerAdminService } from 'src/app/shared/services/event-manager/event-manager-admin.service';

// Ergänzt GlobalStat um ein rein clientseitiges Flag: markiert Einträge, die
// beim Verbinden aus der DB nachgeladen wurden (alte Session), im Gegensatz zu
// Einträgen, die live über den Socket in der aktuellen Session eintreffen.
// `requestId` ist bei vielen serverseitigen Stats (ToolsAvailable, tool runstart,
// CustomTool triggered/finish, chatcompletion, ...) bereits konsistent pro
// Verarbeitungsschritt (eine Nutzereingabe bis zur fertigen Bot-Antwort) gesetzt
// und dient hier als Gruppierungs-Schlüssel.
type DebugLogEntry = GlobalStat & { isHistorical?: boolean; requestId?: string };

// Eine Gruppe aller Debug-Einträge mit derselben requestId (= ein "Verarbeitungsschritt":
// Nutzereingabe → ggf. Tool-Aufrufe → fertige Bot-Antwort). `requestId === null` bedeutet
// ein eigenständiges Ereignis ohne Zuordnung (z.B. blockly_debug, Skill-Änderungen).
type DebugLogGroup = {
  kind: 'group';
  requestId: string | null;
  entries: DebugLogEntry[];
  isHistorical: boolean;
};

// Eine "Runde": RAGAI.FLOW.requestStart + der/die dazwischenliegende(n)
// Verarbeitungsschritt(e) + RAGAI.FLOW.requestComplete, zusammengefasst zu EINER
// visuellen Einheit, da diese drei Teile immer zusammengehören (eine komplette
// Anfrage-Antwort-Runde).
type DebugLogRound = {
  kind: 'round';
  groups: DebugLogGroup[];
  isHistorical: boolean;
};

type DebugLogRow = DebugLogGroup | DebugLogRound;

// "Flow-Mode" (gruppiert, nur ausgewählte Kategorien) vs. "Detail-Mode"
// (tabellarisch, ausnahmslos alle Events, gefiltert per Freitext-startsWith).
type ViewMode = 'flow' | 'detail';

const EVENT_FILTER_STORAGE_KEY = 'debug-view-enabled-event-prefixes';
const DEFAULT_ENABLED_EVENT_PREFIXES = ['RAGAI.FLOW.'];
const VIEW_MODE_STORAGE_KEY = 'debug-view-mode';

@Component({
  selector: 'app-debug-view',
  standalone: true,
  imports: [DatePipe, NgTemplateOutlet,  FormsModule, TagModule, ButtonModule, TooltipModule],
  templateUrl: './debug-view.component.html',
  styleUrls: ['./debug-view.component.scss']
})
export class DebugViewComponent implements OnInit, OnDestroy {
  @Input() public botId = '';
  @ViewChild('logContainer') private logContainer!: ElementRef<HTMLElement>;

  public debugLog: DebugLogEntry[] = [];
  public connected = false;
  public autoScroll = true;
  public viewMode: ViewMode = this.loadViewMode();
  public detailFilterText = '';

  public readonly eventCategories: { prefix: string; label: string }[] = RAGAI_DEBUG_EVENT_PREFIXES.map(prefix => {
    const found = EventsStringService.alleventsList.find(([key]) => key === prefix + '*');
    const fallback = prefix.replace('RAGAI.', '').replace(/\.$/, '');
    const label = found ? found[1].replace(/:\s*Alle\s*$/i, '').trim() : fallback.charAt(0) + fallback.slice(1).toLowerCase();
    return { prefix, label };
  });
  public readonly enabledEventPrefixes = new Set<string>(this.loadEnabledEventPrefixes());

  // Verarbeitungsschritte sind initial immer eingeklappt (Übersicht), bis der
  // User sie aktiv aufklappt.
  public readonly expandedGroupRequestIds = new Set<string>();

  private readonly destroy$ = new Subject<void>();

  public readonly connectedLabel = $localize`:@@debug-view-connected:Verbunden`;
  public readonly connectingLabel = $localize`:@@debug-view-connecting:Verbinde…`;
  public readonly autoScrollLabel = $localize`:@@debug-view-auto-scroll:Auto-Scroll`;
  public readonly clearLabel = $localize`:@@debug-view-clear:Log leeren`;
  public readonly detailsLabel = $localize`:@@debug-view-show-details:Details`;
  public readonly instructionsLabel = $localize`:@@debug-view-instructions-label:Instructions`;
  public readonly flowModeLabel = $localize`:@@debug-view-mode-flow:Flow-Mode`;
  public readonly detailModeLabel = $localize`:@@debug-view-mode-detail:Detail-Mode`;
  public readonly detailFilterPlaceholder = $localize`:@@debug-view-detail-filter-placeholder:Filter (beginnt mit) z.B. RAGAI.MSG.`;
  public readonly originFrontendLabel = $localize`:@@debug-view-origin-frontend:Frontend`;
  public readonly originServerLabel = $localize`:@@debug-view-origin-server:Server`;

  private io: WsClient | null = null;
  private readonly connectHandler = () => {
    this.connected = true;
    this.io?.emit('registerDebug', { register: true }, (data: { currentStats: GlobalStat[] }) => {
      data.currentStats.forEach((stat: GlobalStat) => {
        stat.created = new Date(stat.created);
        const entry: DebugLogEntry = { ...stat, isHistorical: true };
        this.debugLog.push(entry);
      });
      if (this.autoScroll) {
        setTimeout(() => this.scrollToBottom(), 10);
      }
    });
  };
  private readonly msgHandler = (data: { stat: GlobalStat }) => {
    data.stat.created = new Date(data.stat.created);
    this.debugLog.push({ ...data.stat, isHistorical: false });
    if (this.autoScroll) {
      setTimeout(() => this.scrollToBottom(), 10);
    }
  };
  private readonly disconnectHandler = () => { this.connected = false; };

  constructor(
    private route: ActivatedRoute,
    private debugSocketService: DebugSocketService,
    private eventManager: EventManagerAdminService
  ) { }

  public async ngOnInit(): Promise<void> {
    if (!this.botId) {
      this.botId = this.route.snapshot.params['id'] as string;
    }

    this.debugSocketService.connectToDebug(this.botId);
    this.io = await this.debugSocketService.getDebugIo();

    this.io.on('connect', this.connectHandler);
    this.io.on('disconnect', this.disconnectHandler);
    this.io.on('debugMsg', this.msgHandler);

    this.connected = this.io.connected;
    if (this.io.connected) {
      // Socket already connected – register immediately (connect event won't fire again)
      this.connectHandler();
    }
    // If not yet connected, connectHandler fires on connect and handles registration.
    // connectHandler also fires on every reconnect, ensuring room membership is restored.

    const RAGAI_EVENT_PREFIXES = RAGAI_DEBUG_EVENT_PREFIXES;
     
    const eventFired$: Subject<{ hook: string; params: unknown }> = this.eventManager.eventFired$;
    eventFired$.pipe(takeUntil(this.destroy$)).subscribe(({ hook, params }) => {
      if (hook === 'RAGAI.DEBUG.log') {
        const payload = params as { title: string; reason?: string };
        this.pushFrontendEntry('blockly_debug', payload.title, payload.reason ?? '');
        return;
      }
      if (RAGAI_EVENT_PREFIXES.some(prefix => hook.startsWith(prefix))) {
        this.pushFrontendEntry('ragai_event', hook, JSON.stringify(params));
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.io) {
      this.io.off('connect', this.connectHandler);
      this.io.off('disconnect', this.disconnectHandler);
      this.io.off('debugMsg', this.msgHandler);
      this.io.emit('registerDebug', { register: false }, () => { });
    }
  }

  public clearLog(): void {
    this.debugLog.splice(0, this.debugLog.length);
  }

  public toggleAutoScroll(): void {
    this.autoScroll = !this.autoScroll;
    if (this.autoScroll) {
      this.scrollToBottom();
    }
  }

  public toggleEventCategory(prefix: string): void {
    if (this.enabledEventPrefixes.has(prefix)) {
      this.enabledEventPrefixes.delete(prefix);
    } else {
      this.enabledEventPrefixes.add(prefix);
    }
    this.saveEnabledEventPrefixes();
  }

  public setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    this.saveViewMode();
  }

  // Detail-Mode: keine Kategorie-Filterung, ausnahmslos alle Events – nur per
  // Freitext-"startsWith" einschränkbar, damit maximale Information erhalten bleibt.
  public get detailFilteredLog(): DebugLogEntry[] {
    const filter = this.detailFilterText.trim().toLowerCase();
    if (!filter) return this.debugLog;
    return this.debugLog.filter(entry => this.getEntrySearchKey(entry).toLowerCase().startsWith(filter));
  }

  public getEntrySearchKey(entry: DebugLogEntry): string {
    return entry.action === 'ragai_event' ? entry.title : entry.action;
  }

  // Frontend-Events (blockly_debug, ragai_event) werden clientseitig im Bot
  // erzeugt und per Socket lediglich an den Debug-Log durchgereicht. Alle
  // anderen Actions entstehen direkt serverseitig während der Anfrageverarbeitung.
  public isFrontendEvent(entry: DebugLogEntry): boolean {
    return entry.action === 'ragai_event' || entry.action === 'blockly_debug';
  }

  public trackByEntryRef(_index: number, entry: DebugLogEntry): DebugLogEntry {
    return entry;
  }

  public get filteredDebugLog(): DebugLogEntry[] {
    return this.debugLog.filter(entry => {
      if (entry.action !== 'ragai_event') return true;
      const prefix = RAGAI_DEBUG_EVENT_PREFIXES.find(p => entry.title.startsWith(p));
      // Events, deren Kategorie nicht zu den kuratierten Flow-Mode-Kategorien
      // gehört (z.B. neue, breiter weitergeleitete Events), werden im Flow-Mode
      // NICHT angezeigt (nur im Detail-Mode) – sonst würde die kuratierte Ansicht
      // durch unbekannte Kategorien geflutet.
      return prefix ? this.enabledEventPrefixes.has(prefix) : false;
    });
  }

  // Fasst alle Einträge mit derselben requestId zu einem "Verarbeitungsschritt"
  // zusammen (Nutzereingabe → Tool-Aufrufe → Bot-Antwort). Bewusst NICHT nur
  // direkt aufeinanderfolgende Einträge, da ToolsAvailable/Tool-Aufrufe/
  // Chatcompletion serverseitig entstehen und clientseitige Bracket-Events
  // (RAGAI.FLOW.requestStart/-Complete) unabhängig davon per Socket eintreffen
  // können – ohne strikte Ankunftsreihenfolge würde die Gruppe sonst reißen.
  public get groupedDebugLog(): DebugLogGroup[] {
    const groups: DebugLogGroup[] = [];
    const groupByRequestId = new Map<string, DebugLogGroup>();
    for (const entry of this.filteredDebugLog) {
      const requestId = entry.requestId ?? undefined;
      if (requestId) {
        const existing = groupByRequestId.get(requestId);
        if (existing) {
          existing.entries.push(entry);
          existing.isHistorical = existing.isHistorical && !!entry.isHistorical;
          continue;
        }
        const group: DebugLogGroup = { kind: 'group', requestId, entries: [entry], isHistorical: !!entry.isHistorical };
        groupByRequestId.set(requestId, group);
        groups.push(group);
        continue;
      }
      groups.push({ kind: 'group', requestId: null, entries: [entry], isHistorical: !!entry.isHistorical });
    }
    return groups;
  }

  // Fasst "RAGAI.FLOW.requestStart" + die dazwischenliegenden Verarbeitungsschritt(e)
  // + "RAGAI.FLOW.requestComplete" zu einer "Runde" zusammen, da diese drei Teile
  // immer eine zusammengehörige Anfrage-Antwort-Einheit bilden.
  public get debugLogRows(): DebugLogRow[] {
    const groups = this.groupedDebugLog;
    const rows: DebugLogRow[] = [];
    let i = 0;
    while (i < groups.length) {
      const group = groups[i];
      if (!group) {
        i++;
        continue;
      }
      if (this.isRequestStartGroup(group)) {
        const roundGroups: DebugLogGroup[] = [group];
        let j = i + 1;
        let closed = false;
        while (j < groups.length) {
          const next = groups[j];
          if (!next) break;
          roundGroups.push(next);
          j++;
          if (this.isRequestCompleteGroup(next)) {
            closed = true;
            break;
          }
        }
        if (closed) {
          this.attachInstructionsToRequestStart(roundGroups);
          rows.push({ kind: 'round', groups: roundGroups, isHistorical: roundGroups.every(g => g.isHistorical) });
          i = j;
          continue;
        }
      }
      rows.push(group);
      i++;
    }
    return rows;
  }

  // Bettet den "instructions" (System Prompt) Eintrag als zusätzliche, aufklappbare
  // Info direkt im "RAGAI.FLOW.requestStart"-Eintrag ein, statt ihn als eigene Zeile
  // innerhalb des Verarbeitungsschritts anzuzeigen.
  private attachInstructionsToRequestStart(roundGroups: DebugLogGroup[]): void {
    const requestStartEntry = roundGroups[0]?.entries[0];
    if (!requestStartEntry) return;
    for (const group of roundGroups) {
      const idx = group.entries.findIndex(e => e.action === 'instructions');
      if (idx === -1) continue;
      const instructionsEntry = group.entries[idx];
      if (instructionsEntry) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        requestStartEntry.explanation = instructionsEntry.explanation ?? instructionsEntry.reason;
      }
      group.entries.splice(idx, 1);
      break;
    }
  }

  private isRequestStartGroup(group: DebugLogGroup): boolean {
    const entry = group.entries[0];
    return group.entries.length === 1 && !!entry && entry.action === 'ragai_event' && entry.title === 'RAGAI.FLOW.requestStart';
  }

  private isRequestCompleteGroup(group: DebugLogGroup): boolean {
    const entry = group.entries[0];
    return group.entries.length === 1 && !!entry && entry.action === 'ragai_event' && entry.title === 'RAGAI.FLOW.requestComplete';
  }

  public showSessionSeparatorForRow(index: number): boolean {
    const rows = this.debugLogRows;
    const row = rows[index];
    if (!row || row.isHistorical) return false;
    const prev = rows[index - 1];
    if (prev) return prev.isHistorical;
    return this.debugLog.some(e => e.isHistorical);
  }

  public toggleGroup(requestId: string | null): void {
    if (!requestId) return;
    if (this.expandedGroupRequestIds.has(requestId)) {
      this.expandedGroupRequestIds.delete(requestId);
    } else {
      this.expandedGroupRequestIds.add(requestId);
    }
  }

  public isGroupExpanded(requestId: string | null): boolean {
    if (!requestId) return true;
    return this.expandedGroupRequestIds.has(requestId);
  }

  public getToolCallCount(group: DebugLogGroup): number {
    return group.entries.filter(e => e.action === 'tool runstart' || e.action === 'CustomTool triggered').length;
  }

  // Solange der Verarbeitungsschritt noch kein chatcompletion-Ergebnis enthält,
  // läuft die Verarbeitung noch (Icon dreht sich); danach dient dasselbe Icon
  // als Auf-/Zu-Klapp-Pfeil.
  public isGroupComplete(group: DebugLogGroup): boolean {
    return group.entries.some(e => e.action === 'chatcompletion');
  }

  public getToolsAvailableCount(group: DebugLogGroup): number | null {
    const stat = group.entries.find(e => e.action === 'ToolsAvailable');
    if (!stat) return null;
    const match = /(\d+)/.exec(stat.title);
    if (!match) return null;
    const num = Number(match[1]);
    return Number.isNaN(num) ? null : num;
  }

  public getGroupTimestamp(group: DebugLogGroup): Date | null {
    return group.entries[0]?.created ?? null;
  }

  public getActionSeverity(action: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (action.toLowerCase().includes('error')) return 'danger';
    if (action === 'chatcompletion') return 'success';
    if (action === 'blockly_debug') return 'info';
    if (action === 'ragai_event') return 'success';
    if (action.startsWith('tool') || action.startsWith('Tool') || action.startsWith('Custom')) return 'warn';
    return 'secondary';
  }

  // Menschenlesbare Kurz-Labels für die Action-Tags im Flow-Modus. Der interne
  // (englische) Action-Name bleibt weiterhin über den Tooltip einsehbar (siehe Template).
  private static readonly ACTION_LABELS: Record<string, string> = {
    avatarbotNewSession: 'Neue Avatar-Session',
    chatcompletion: 'KI-Antwort',
    heygen_streaming_avatar: 'Avatar-Streaming',
    'Membot call to chat/botid done': 'Membot-Aufruf',
    skillenabled: 'Skill aktiviert',
    skilldisabled: 'Skill deaktiviert',
    skillsforced: 'Skill erzwungen',
    texttospeech: 'Text-zu-Sprache',
    ThreadbotServiceCreatedThread: 'Thread erstellt',
    ThreadbotServiceUpdatedAssistent: 'Assistent aktualisiert',
    ThreadbotService: 'Thread-Service',
    ThreadbotServiceCreatedAssistent: 'Assistent erstellt',
    ToolServiceToolNotFound: 'Tool nicht gefunden',
    ToolsAvailable: 'Verfügbare Tools',
    'tool has Contents from url': 'Inhalt von URL geladen',
    'tool Has outputs': 'Tool-Ausgabe',
    toolrunerror: 'Tool-Fehler',
    'tool runsMarkdown': 'Markdown-Ausgabe',
    'tool runstart': 'Tool gestartet',
    toolAddDatatrunkTool: 'Datenbank-Tool',
    'CustomTool finish': 'Wenn-Dann abgeschlossen',
    'CustomTool triggered': 'Wenn-Dann ausgelöst',
    'Waiting RunStart @OpenAI': 'Warte auf OpenAI',
    'Requesting Run @OpenAI': 'Anfrage an OpenAI',
    blockly_debug: 'Blockly-Debug',
    ragai_event: 'Ereignis',
    mcp: 'MCP',
    'doc-search': 'Dokumentensuche',
    instructions: 'Anweisungen'
  };

  public getActionLabel(action: string): string {
    return DebugViewComponent.ACTION_LABELS[action] ?? action;
  }

  public formatExplanation(explanation: unknown): string {
    if (typeof explanation === 'string') return explanation;
    return JSON.stringify(explanation, null, 2);
  }

  public getExplanationLabel(entry: DebugLogEntry): string {
    return entry.title === 'RAGAI.FLOW.requestStart' ? this.instructionsLabel : this.detailsLabel;
  }

  // Bei "RAGAI.FLOW.requestStart" ist entry.reason der eigentliche pInput-Text
  // (die Nutzereingabe) und soll deshalb prominent OBERHALB der Kopfzeile stehen,
  // statt als gedimmter Text darunter wie bei allen anderen Einträgen.
  public isRequestStartWithText(entry: DebugLogEntry): boolean {
    return entry.title === 'RAGAI.FLOW.requestStart' && !!entry.reason;
  }

  public formatDuration(ms: number): string {
    if (ms < 1000) return `${String(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }

  public getEntryTitle(entry: GlobalStat): string {
    if (entry.action !== 'ragai_event') return entry.title;
    const found = EventsStringService.alleventsList.find(([key]) => key === entry.title);
    return found ? found[1] : entry.title;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  // groupedDebugLog/debugLogRows sind Getter, die bei jedem Change-Detection-
  // Zyklus frisch neue Group-Wrapper-Objekte erzeugen (auch wenn die zugrunde
  // liegenden Einträge unverändert sind). Ohne stabile trackBy-Identität würde
  // Angular den DOM-Teilbaum (inkl. nativer <details>-Elemente) bei jedem Zyklus
  // zerstören und neu aufbauen, wodurch z.B. das Aufklappen von "Details" sofort
  // wieder zurückgesetzt wird. Die einzelnen Entry-Objekte bleiben dagegen über
  // Getter-Aufrufe hinweg referenzstabil, daher hier als Tracking-Schlüssel nutzen.
  public trackByGroup(index: number, group: DebugLogGroup): unknown {
    return group.entries[0] ?? index;
  }

  public trackByRow(index: number, row: DebugLogRow): unknown {
    if (row.kind === 'round') {
      return row.groups[0]?.entries[0] ?? index;
    }
    return row.entries[0] ?? index;
  }

  private pushFrontendEntry(action: 'blockly_debug' | 'ragai_event', title: string, reason: string): void {
    this.debugLog.push({
      action,
      title,
      reason,
      isHistorical: false,
      context: { botid: this.botId, user: '' },
      created: new Date(),
      executionDuration: 0,
      openAiMessageId: ''
    });
    if (this.autoScroll) {
      setTimeout(() => this.scrollToBottom(), 10);
    }
  }

  private scrollToBottom(): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (this.logContainer?.nativeElement) {
      this.logContainer.nativeElement.scrollTop = this.logContainer.nativeElement.scrollHeight;
    }
  }

  private loadEnabledEventPrefixes(): string[] {
    try {
      const raw = localStorage.getItem(EVENT_FILTER_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed) && parsed.length > 0) {
          const valid = parsed.filter((p): p is string => (RAGAI_DEBUG_EVENT_PREFIXES as readonly string[]).includes(p as string));
          if (valid.length > 0) return valid;
        }
      }
    } catch {
      // Ungültiger localStorage-Inhalt – Default verwenden
    }
    return [...DEFAULT_ENABLED_EVENT_PREFIXES];
  }

  private saveEnabledEventPrefixes(): void {
    localStorage.setItem(EVENT_FILTER_STORAGE_KEY, JSON.stringify(Array.from(this.enabledEventPrefixes)));
  }

  private loadViewMode(): ViewMode {
    try {
      const raw = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      if (raw === 'flow' || raw === 'detail') return raw;
    } catch {
      // Ungültiger localStorage-Inhalt – Default verwenden
    }
    return 'flow';
  }

  private saveViewMode(): void {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, this.viewMode);
  }
}
